<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <h1 class="h2 mb-1 fw-bold">Field Tests</h1>
    <p class="text-muted mb-0">Record and track pre-flight, post-flight, and equipment tests</p>
  </div>
  <div class="d-flex gap-2">
    <button class="btn btn-outline-primary" (click)="uploadCustomTest()">
      <i class="bi bi-cloud-upload me-2"></i>Upload Field Test
    </button>
    <button class="btn btn-primary" (click)="addFieldTest()">
      <i class="bi bi-plus-lg me-2"></i>New Field Test
    </button>
  </div>
</div>

<!-- Status Summary Cards -->
<div class="row g-3 mb-4">
  <div class="col-4">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-success-subtle text-success">
            <i class="bi bi-check-circle"></i>
          </div>
          <div>
            <div class="stat-value text-success">{{ passedCount() }}</div>
            <div class="stat-label text-muted">Passed</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-4">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-danger-subtle text-danger">
            <i class="bi bi-x-circle"></i>
          </div>
          <div>
            <div class="stat-value text-danger">{{ failedCount() }}</div>
            <div class="stat-label text-muted">Failed</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-4">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-warning-subtle text-warning">
            <i class="bi bi-clock"></i>
          </div>
          <div>
            <div class="stat-value text-warning">{{ pendingCount() }}</div>
            <div class="stat-label text-muted">Pending</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Tab Navigation -->
<ul class="nav nav-tabs mb-3">
  <li class="nav-item">
    <button class="nav-link" [class.active]="activeTab() === 'records'" (click)="activeTab.set('records')">
      <i class="bi bi-clipboard-data me-2"></i>Test Records
      <span class="badge bg-secondary ms-2">{{ fieldTests().length }}</span>
    </button>
  </li>
  <li class="nav-item">
    <button class="nav-link" [class.active]="activeTab() === 'templates'" (click)="activeTab.set('templates')">
      <i class="bi bi-file-earmark-text me-2"></i>Uploaded Tests
      <span class="badge bg-secondary ms-2">{{ customTests().length }}</span>
    </button>
  </li>
</ul>

<!-- Test Records Tab -->
<div *ngIf="activeTab() === 'records'">
  <div class="card">
    <div class="card-header d-flex justify-content-between align-items-center p-3">
      <h5 class="mb-0">Recent Field Tests</h5>
      <span class="badge bg-secondary">{{ fieldTests().length }} tests</span>
    </div>
    <div class="card-body p-0">
      <ng-container *ngIf="!isLoading() && fieldTests().length > 0; else emptyRecordsState">
        <ng-container *ngFor="let test of fieldTests(); let last = last">
          <div class="test-item p-3" [class.border-bottom]="!last">
            <div class="d-flex justify-content-between align-items-start">
              <div class="d-flex align-items-start gap-3 flex-grow-1">
                <div class="test-icon" [class.test-icon-passed]="test.status === 'passed'" [class.test-icon-failed]="test.status === 'failed'" [class.test-icon-pending]="test.status === 'pending'">
                  <i class="bi" [ngClass]="{
                    'bi-check-lg': test.status === 'passed',
                    'bi-x-lg': test.status === 'failed',
                    'bi-clock': test.status === 'pending'
                  }"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <h6 class="mb-0 fw-medium">{{ test.title }}</h6>
                    <span class="badge" [ngClass]="getTestTypeBadgeClass(test.testType)">
                      {{ getTestTypeLabel(test.testType) }}
                    </span>
                    <span class="badge" [ngClass]="test.status | statusBadge:'test'">
                      {{ getStatusText(test.status) }}
                    </span>
                  </div>

                  <div class="d-flex flex-wrap gap-3 small mb-2">
                    <div>
                      <i class="icon-drone me-1 text-muted"></i>
                      <span>{{ test.aircraft }}</span>
                    </div>
                    <div>
                      <i class="bi bi-person me-1 text-muted"></i>
                      <span>{{ test.pilot }}</span>
                    </div>
                    <div>
                      <i class="bi bi-geo-alt me-1 text-muted"></i>
                      <span>{{ test.location }}</span>
                    </div>
                    <div>
                      <i class="bi bi-calendar me-1 text-muted"></i>
                      <span>{{ test.date | adnDate }}</span>
                    </div>
                  </div>

                  <div class="d-flex align-items-center gap-2 small">
                    <span class="text-muted">Checklist:</span>
                    <span class="fw-medium" [class.text-success]="getPassedChecklistCount(test) === test.checklist.length" [class.text-warning]="getPassedChecklistCount(test) < test.checklist.length">
                      {{ getPassedChecklistCount(test) }}/{{ test.checklist.length }} passed
                    </span>
                  </div>

                  <div *ngIf="test.notes" class="mt-2">
                    <span class="small text-muted fst-italic">
                      <i class="bi bi-chat-left-text me-1"></i>{{ test.notes }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="flex-shrink-0 ms-3">
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" (click)="viewTest(test)" title="View details">
                    <i class="bi bi-eye"></i>
                  </button>
                  <button class="btn btn-outline-secondary" (click)="editTest(test)" title="Edit">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button class="btn btn-outline-secondary text-danger" (click)="deleteTest(test)" title="Delete">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ng-container>
      </ng-container>

      <ng-template #emptyRecordsState>
        <div class="p-5 text-center">
          <div class="empty-state-icon mb-3">
            <i class="bi bi-clipboard-check"></i>
          </div>
          <h5>No Field Tests Yet</h5>
          <p class="text-muted mb-3">Record your first field test to start tracking equipment inspections and checks.</p>
          <button class="btn btn-primary" (click)="addFieldTest()">
            <i class="bi bi-plus-lg me-2"></i>New Field Test
          </button>
        </div>
      </ng-template>
    </div>
  </div>
</div>

<!-- Custom Templates Tab -->
<div *ngIf="activeTab() === 'templates'">
  <div class="card">
    <div class="card-header d-flex justify-content-between align-items-center p-3">
      <h5 class="mb-0">Uploaded Field Tests</h5>
      <button class="btn btn-sm btn-primary" (click)="uploadCustomTest()">
        <i class="bi bi-cloud-upload me-2"></i>Upload Field Test
      </button>
    </div>
    <div class="card-body p-0">
      <ng-container *ngIf="customTests().length > 0; else emptyTemplatesState">
        <ng-container *ngFor="let test of customTests(); let last = last">
          <div class="template-item p-3" [class.border-bottom]="!last">
            <div class="d-flex justify-content-between align-items-start">
              <div class="d-flex align-items-start gap-3 flex-grow-1">
                <div class="template-icon">
                  <i class="bi" [ngClass]="getCustomTestTypeIcon(test.testType)"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <h6 class="mb-0 fw-medium">{{ test.title }}</h6>
                    <span class="badge bg-info">{{ getCustomTestTypeLabel(test.testType) }}</span>
                    <span class="badge bg-secondary">{{ getCategoryLabel(test.category) }}</span>
                    <span *ngIf="test.requiresSignoff" class="badge bg-warning text-dark">
                      <i class="bi bi-pen me-1"></i>Signoff Required
                    </span>
                  </div>

                  <p class="small text-muted mb-2" *ngIf="test.description">{{ test.description }}</p>

                  <div class="d-flex flex-wrap gap-3 small mb-2">
                    <div>
                      <i class="icon-drone me-1 text-muted"></i>
                      <span>{{ getAircraftDisplayName(test.applicableAircraft) }}</span>
                    </div>
                    <div>
                      <i class="bi bi-arrow-repeat me-1 text-muted"></i>
                      <span>{{ getFrequencyLabel(test.frequency) }}</span>
                    </div>
                    <div>
                      <i class="bi bi-calendar me-1 text-muted"></i>
                      <span>Performed {{ test.datePerformed | adnDate }}</span>
                    </div>
                    <div>
                      <i class="bi bi-bar-chart me-1 text-muted"></i>
                      <span>Used {{ test.usageCount }} times</span>
                    </div>
                  </div>

                  <!-- Attached Files -->
                  <div class="file-chips">
                    <span *ngFor="let file of test.files" class="file-chip" (click)="viewCustomTestFile(file)">
                      <i class="bi me-1" [ngClass]="getFileIcon(file.type)"></i>
                      {{ file.name }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="flex-shrink-0 ms-3">
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" (click)="viewCustomTest(test)" title="View details">
                    <i class="bi bi-eye"></i>
                  </button>
                  <button class="btn btn-outline-secondary" (click)="editCustomTest(test)" title="Edit">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button class="btn btn-outline-secondary text-danger" (click)="deleteCustomTest(test)" title="Delete">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ng-container>
      </ng-container>

      <ng-template #emptyTemplatesState>
        <div class="p-5 text-center">
          <div class="empty-state-icon mb-3">
            <i class="bi bi-cloud-upload"></i>
          </div>
          <h5>No Uploaded Field Tests</h5>
          <p class="text-muted mb-3">Upload scanned field tests, calibration records, or equipment inspection forms for compliance records.</p>
          <button class="btn btn-primary" (click)="uploadCustomTest()">
            <i class="bi bi-cloud-upload me-2"></i>Upload Field Test
          </button>
        </div>
      </ng-template>
    </div>
  </div>
</div>
